<template lang="pug">
  .product-tile(:class="tileClass", :style="{ backgroundColor: tileColor }" )
    a.link--unstyled(
      :is="noLink ? 'div': 'a'"
      :href="product.url"
    )
      .product-tile__media
        .product-tile__media-wrapper(v-if="image")
          img.product-tile__image(:src="currentImage", :alt="image.alt" :draggable="false")
          .product-tile__badge(v-if="productBadge")
            .product-tile__badge__text {{ productBadge }}
      .product-tile__content
        .product-tile__details
          h3.product-tile__title {{ product.title }}
          .product-tile__tag(v-if="subHeading") {{ subHeading }}

        .product-tile__pricing
          p.product-tile__price(v-if="isOnSale")
            | {{ product.price | moneyWithoutDecimals }}
            s.product-tile__price.product-tile__price--original {{ comparePrice | moneyWithoutDecimals }}
          p.product-tile__price(v-else) {{ product.price | moneyWithoutDecimals }}
    form.product-tile__form(@submit.prevent="addToCart", novalidate)
      base-button.product-tile__form-submit(
        :disabled="isDisabled"
        :loading="adding"
      ) {{ addToCartText }}

</template>

<script>
  import { getProductBadge } from 'scripts/helpers/tags.js';
  import { getSizedImageUrl } from 'scripts/helpers/images.js';

  import BaseButton from 'scripts/components/buttons/BaseButton.vue';

  export default {
    name: 'ProductTile',
    components: { BaseButton },
    props: {
      product: Object,
      disableHover: {
        type: Boolean,
        default: false,
      },
      horizontal: {
        type: Boolean,
        default: false,
      },
      tileColor: String,
      noLink: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        hovered: false,
        adding: false,
        quantity: 1,
      };
    },
    computed: {
      tileClass() {
        return {
          'disable-hover': this.disableHover,
          'product-tile--horizontal': this.horizontal,
        };
      },
      image() {
        const firstImage = this.product.images[0];

        return {
          src: getSizedImageUrl(firstImage.src, '450x'),
          alt: firstImage.alt,
        };
      },
      hoverImage() {
        const image = this.product.images[1];

        if (!image) return null;

        return {
          src: getSizedImageUrl(image.src, '450x'),
          alt: image.alt,
        };
      },
      currentImage() {
        return this.hovered && !this.disableHover && this.hoverImage ? this.hoverImage.src : this.image.src;
      },
      productBadge() {
        return getProductBadge(this.product.tags);
      },
      comparePrice() {
        const price = this.product.variants[0].compare_at_price;
        return !Number.isNaN(price) ? price : this.product.price;
      },
      isOnSale() {
        return this.comparePrice > this.product.price;
      },
      subHeading() {
        if (!this.product.customFields.subheading) return false;

        return this.product.customFields.subheading;
      },
      soldOut() {
        return this.product.available === 'false';
      },
      isDisabled() {
        return this.adding || this.soldOut;
      },
      addToCartText() {
        return this.soldOut ? 'Sold Out' : 'Add to Cart';
      },
    },
    methods: {
      enter() {
        if (!this.disableHover) this.hovered = true;
      },
      leave() {
        this.hovered = false;
      },
      addToCart() {
        const addProduct = {
          id: this.product.variants[0].id,
          quantity: this.quantity,
        };

        this.adding = true;

        this.$store
          .dispatch('cart/addItem', addProduct)
          .then(() => this.$store.dispatch('cart/openSidecart'))
          .catch((error) => this.$store.dispatch('toast/send', { text: error, type: 'error' }))
          .then(() => {
            this.adding = false;
          });
      },
    },
  };
</script>

<style scoped lang="scss">
  .product-tile {
    display: flex;
    flex-direction: column;
    position: relative;
    margin: 2px;
    padding: 0;
    width: 100%;
    max-width: rem(316px);
    text-align: left;
    text-decoration: none;
  }

  .product-tile--horizontal {
    padding: 12px;
    flex-direction: row-reverse;
    text-align: left;
    justify-content: space-between;
  }

  .product-tile__media {
    overflow: hidden;
  }

  .product-tile__media-wrapper {
    position: relative;
    padding-bottom: 100%;
    height: 0;
  }

  .product-tile__image {
    margin: 0 auto 20px;
    position: absolute;
    top: 0;
    height: 100%;
    width: 100%;
    object-fit: contain;
  }

  .product-tile__content {
    padding: ($spacing-unit * 3) ($spacing-unit * 4);
    color: $color-black;
    text-align: center;

    .product-recommendations & {
      display: none;
    }
  }

  .product-tile__title {
    margin: 0;
    color: $color-black;
    font-size: rem(16);
    font-weight: 500;
    line-height: 1.4;
    text-transform: uppercase;
  }

  .product-tile__tag {
    margin: 0;
    color: $color-green-900;
    font-size: rem(11px);
    text-align: left;
    text-transform: uppercase;

    @include tablet-up {
      font-size: $font-size-body-small;
    }
  }

  .product-tile__quantity {
    margin-top: 12px;
    font-weight: $font-weight-bold;
    transition: 0.3s ease all;
  }

  .product-tile__quantity--limited {
    color: $text--accent;
  }

  .product-tile__quantity--oos {
    color: $text--mid;
  }

  .product-tile__price {
    margin: 0;
    color: $color-black;
    font-size: rem(14px);
    line-height: rem(18px);
    letter-spacing: rem(0.88px);

    @include tablet-up {
      font-size: rem(16px);
      line-height: rem(20px);
      letter-spacing: rem(1px);
    }

    .product-tile--horizontal & {
      font-size: rem(18);
    }
  }

  .product-tile__price--original {
    margin: 0 $spacing-unit;
    color: $color-green-900;
  }

  .product-tile__form {
    display: flex;
    justify-content: center;

    .product-recommendations & {
      display: none;
    }
  }
</style>
